import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { resolveAssetUrl } from "../../lib/api";
import { ArrowRight, Clock, Layers, Users, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QV_LOGO =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

const DEFAULT_COVER =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/z57p91jo_IMG_2623%202.PNG";

const PublicNav = () => (
  <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3">
        <img src={QV_LOGO} alt="QV" className="h-10 w-10 object-contain" />
        <div className="leading-tight hidden sm:block">
          <div className="qv-display text-white text-lg">QUIQUE VARGAS</div>
          <div className="text-[10px] tracking-[0.32em] text-neutral-400 uppercase">Programas</div>
        </div>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.22em] text-neutral-300">
        <a href="/" className="hover:text-white transition-colors">Inicio</a>
        <a href="/programas" className="text-white">Programas</a>
        <a href="/#contacto" className="hover:text-white transition-colors">Asesoría 1:1</a>
      </nav>
      <a href="/app/login" className="qv-btn-ghost text-xs" data-testid="nav-login">
        MI CUENTA <ArrowRight size={16} />
      </a>
    </div>
  </header>
);

const PublicFooter = () => (
  <footer className="border-t border-white/10 py-10 mt-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center text-xs text-neutral-500 tracking-[0.18em] uppercase">
      © {new Date().getFullYear()} Quique Vargas · Performance Coach
    </div>
  </footer>
);

const ProgramCard = ({ p }) => (
  <Link
    to={`/programas/${p.slug}`}
    className="qv-testi-card group flex flex-col overflow-hidden"
    data-testid={`program-card-${p.slug}`}
  >
    <div className="qv-testi-photo">
      <img src={resolveAssetUrl(p.cover_image) || DEFAULT_COVER} alt={p.title} loading="lazy" />
      {p.level && (
        <div className="qv-testi-badge">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--qv-red)" }} />
          {p.level}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none" />
      <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-md px-3 py-1.5 border border-white/10 qv-display text-white text-lg">
        {Math.round(p.price_eur)}€
      </div>
    </div>
    <div className="p-6 flex flex-col gap-4 flex-1">
      <div>
        <h3 className="qv-display text-white text-2xl leading-tight group-hover:text-[color:var(--qv-red)] transition-colors">
          {p.title}
        </h3>
        {p.subtitle && <p className="text-neutral-400 text-sm mt-2">{p.subtitle}</p>}
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-neutral-300">
        <span className="inline-flex items-center gap-1.5">
          <Clock size={13} color="var(--qv-red)" /> {p.duration_weeks} semanas
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Layers size={13} color="var(--qv-red)" /> {p.total_sessions} sesiones
        </span>
      </div>

      <div className="mt-auto pt-4 border-t border-white/10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-white">
        Ver programa <ArrowRight size={14} />
      </div>
    </div>
  </Link>
);

export default function ProgramsCatalog() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/programs`);
        if (!alive) return;
        setPrograms(data);
      } catch (e) {
        if (!alive) return;
        setError("No se pueden cargar los programas ahora mismo.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="programs-catalog">
      <PublicNav />

      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 overflow-hidden qv-grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="qv-app-glow" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="qv-overline">PROGRAMAS</div>
          <h1 className="qv-display text-white text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.9] max-w-4xl">
            ENTRENAMIENTOS PROBADOS.<br />
            <span style={{ color: "var(--qv-red)" }}>LISTOS PARA TI.</span>
          </h1>
          <p className="mt-6 text-neutral-300 max-w-2xl text-base sm:text-lg leading-relaxed">
            Programas 100% estructurados que puedes empezar hoy. Pago único,
            acceso de por vida y actualizaciones incluidas.
          </p>
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {loading && (
            <div className="text-neutral-500 text-sm">Cargando programas...</div>
          )}
          {!loading && error && (
            <div className="text-neutral-400 text-sm">{error}</div>
          )}
          {!loading && !error && programs.length === 0 && (
            <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center">
              <div className="qv-overline">PRÓXIMAMENTE</div>
              <h2 className="qv-display text-white text-3xl sm:text-4xl mt-3">
                Nuevos programas en camino.
              </h2>
              <p className="mt-4 text-neutral-400 text-sm max-w-md mx-auto">
                Estamos preparando el primer lote. Mientras tanto puedes contratar la asesoría 1:1.
              </p>
              <a href="/#contacto" className="qv-btn-primary mt-8 inline-flex">
                RESERVAR ASESORÍA <ArrowRight size={16} />
              </a>
            </div>
          )}
          {!loading && programs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="programs-grid">
              {programs.map((p) => <ProgramCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-16 border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Target, title: "Sin plantillas genéricas", sub: "Cada programa está diseñado con la misma metodología que uso 1:1." },
            { icon: Users, title: "Vídeos de cada ejercicio", sub: "Sabes exactamente cómo hacer cada movimiento." },
            { icon: Clock, title: "Acceso de por vida", sub: "Compra una vez, entrenas cuando quieras. Actualizaciones incluidas." },
          ].map(({ icon: Icon, title, sub }) => (
            <div key={title} className="flex items-start gap-4">
              <Icon size={22} color="var(--qv-red)" className="shrink-0 mt-1" />
              <div>
                <div className="qv-display text-white text-xl">{title}</div>
                <p className="text-neutral-400 text-sm mt-1">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
